import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert,
  Divider,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Pagination,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  DynamicFormOutlined as DynamicFormOutlinedIcon,
  Inbox as InboxIcon,
  MarkEmailRead as MarkReadIcon,
  MarkEmailUnread as MarkUnreadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import {
  getCustomForms,
  saveCustomForm,
  deleteCustomForm,
  getCustomFormSubmissions,
  markCustomFormSubmissionRead,
  deleteCustomFormSubmission,
  CustomFormData,
  CustomFormField,
  CustomFormFieldType,
  CustomFormSubmission
} from '../../../services/ecommerceApi';

interface FormsSettingsProps {
  companyId: string;
  configId: string;
}

interface FormItem {
  id: string;
  configId: string;
  title: string;
  slug: string;
  description: string;
  submitButtonText: string;
  successMessage: string;
  notifyEmail: string;
  fields: CustomFormField[];
  isActive: boolean;
  displayOrder: number;
  isDirty?: boolean;
  isNew?: boolean;
}

const FIELD_TYPE_OPTIONS: { value: CustomFormFieldType; label: string }[] = [
  { value: 'text', label: 'Tek Satır Metin' },
  { value: 'textarea', label: 'Uzun Metin' },
  { value: 'email', label: 'E-posta' },
  { value: 'phone', label: 'Telefon' },
  { value: 'number', label: 'Sayı' },
  { value: 'city_district', label: 'Şehir + İlçe (Hazır liste)' },
  { value: 'select', label: 'Açılır Liste' },
  { value: 'radio', label: 'Tek Seçim (Radio)' },
  { value: 'checkbox', label: 'Çoklu Seçim (Checkbox)' },
  { value: 'date', label: 'Tarih' }
];

const HAS_OPTIONS = (t: CustomFormFieldType) => t === 'select' || t === 'radio' || t === 'checkbox';

// =============================================================================
// Helpers
// =============================================================================

const slugify = (input: string): string => {
  if (!input) return '';
  const replacements: Record<string, string> = {
    ı: 'i', İ: 'i', ş: 's', Ş: 's', ç: 'c', Ç: 'c',
    ğ: 'g', Ğ: 'g', ü: 'u', Ü: 'u', ö: 'o', Ö: 'o'
  };
  let s = input.toLowerCase();
  s = s.split('').map((ch) => replacements[ch] ?? ch).join('');
  s = s.replace(/[^a-z0-9]+/g, '-');
  s = s.replace(/^-+|-+$/g, '');
  return s;
};

const newFieldId = (): string => `f_${Math.random().toString(36).slice(2, 8)}_${Date.now().toString(36)}`;

const formatSubmissionDate = (raw: string): string => {
  if (!raw) return '—';

  const value = String(raw).trim();
  if (!value) return '—';

  // Backend bazı kayıtlarda timezone suffix (Z/+03:00) göndermeyebiliyor.
  // Böyle durumlarda değeri UTC kabul edip TR saatine çeviriyoruz.
  const hasTimezone = /([zZ]|[+\-]\d{2}:\d{2})$/.test(value);
  const normalized = hasTimezone ? value : `${value}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Europe/Istanbul'
  }).format(date);
};

const parseFieldsJson = (raw: unknown): CustomFormField[] => {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((f) => f && typeof f === 'object')
      .map((f: any) => ({
        id: String(f.id ?? newFieldId()),
        type: (FIELD_TYPE_OPTIONS.some((o) => o.value === f.type) ? f.type : 'text') as CustomFormFieldType,
        label: String(f.label ?? ''),
        placeholder: f.placeholder != null ? String(f.placeholder) : '',
        helpText: f.helpText != null ? String(f.helpText) : '',
        required: !!f.required,
        options: Array.isArray(f.options) ? f.options.map((o: any) => String(o)) : undefined
      }));
  } catch {
    return [];
  }
};

const normalizeFieldKey = (value: string): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const fromApi = (raw: any): FormItem => ({
  id: String(raw?.id ?? ''),
  configId: String(raw?.configId ?? ''),
  title: String(raw?.title ?? ''),
  slug: String(raw?.slug ?? ''),
  description: String(raw?.description ?? ''),
  submitButtonText: String(raw?.submitButtonText ?? 'Gönder'),
  successMessage: String(raw?.successMessage ?? 'Mesajınız iletildi. Teşekkür ederiz.'),
  notifyEmail: String(raw?.notifyEmail ?? ''),
  fields: parseFieldsJson(raw?.fieldsJson),
  isActive: raw?.isActive !== false,
  displayOrder: Number(raw?.displayOrder ?? 0)
});

// =============================================================================
// Component
// =============================================================================

const FormsSettings: React.FC<FormsSettingsProps> = ({ companyId, configId }) => {
  const [forms, setForms] = useState<FormItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'success'
  });
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id?: string; isNew?: boolean; title?: string }>({ open: false });
  const [activeSubTab, setActiveSubTab] = useState<'edit' | 'submissions'>('edit');

  const activeForm = forms[activeIndex];

  // ----- Fetch list -----
  const fetchForms = useCallback(async () => {
    if (!configId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await getCustomForms(configId);
      const items = Array.isArray(res?.data) ? res.data.map(fromApi) : [];
      setForms(items);
      setActiveIndex((prev) => (items.length === 0 ? 0 : Math.min(prev, items.length - 1)));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Formlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [configId]);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  // ----- Form mutation helpers -----
  const updateActive = (patch: Partial<FormItem>) => {
    setForms((prev) => prev.map((f, idx) => (idx === activeIndex ? { ...f, ...patch, isDirty: true } : f)));
  };

  const handleAddForm = () => {
    const newItem: FormItem = {
      id: `new_${Date.now()}`,
      configId,
      title: 'Yeni Form',
      slug: '',
      description: '',
      submitButtonText: 'Gönder',
      successMessage: 'Mesajınız iletildi. Teşekkür ederiz.',
      notifyEmail: '',
      fields: [
        { id: newFieldId(), type: 'text', label: 'Ad Soyad', placeholder: '', required: true },
        { id: newFieldId(), type: 'email', label: 'E-posta', placeholder: '', required: true },
        { id: newFieldId(), type: 'phone', label: 'Telefon', placeholder: '', required: false },
        { id: newFieldId(), type: 'textarea', label: 'Mesajınız', placeholder: '', required: true }
      ],
      isActive: true,
      displayOrder: forms.length,
      isDirty: true,
      isNew: true
    };
    setForms((prev) => [...prev, newItem]);
    setActiveIndex(forms.length);
    setActiveSubTab('edit');
  };

  const handleSave = async () => {
    if (!activeForm) return;
    if (!activeForm.title.trim()) {
      setSnack({ open: true, message: 'Form başlığı zorunludur', severity: 'error' });
      return;
    }
    if (activeForm.fields.length === 0) {
      setSnack({ open: true, message: 'En az bir alan eklemelisiniz', severity: 'error' });
      return;
    }
    // Field doğrulama
    for (const f of activeForm.fields) {
      if (!f.label.trim()) {
        setSnack({ open: true, message: 'Her alan için bir etiket (label) gereklidir', severity: 'error' });
        return;
      }
      if (HAS_OPTIONS(f.type) && (!f.options || f.options.length === 0)) {
        setSnack({ open: true, message: `'${f.label}' alanı için en az bir seçenek girilmelidir`, severity: 'error' });
        return;
      }
    }

    const slugCandidate = (activeForm.slug?.trim() || slugify(activeForm.title));
    if (!slugCandidate) {
      setSnack({ open: true, message: 'Geçerli bir kısa ad (slug) üretilemedi', severity: 'error' });
      return;
    }

    try {
      setSaving(true);
      const payload: CustomFormData = {
        id: activeForm.isNew ? undefined : activeForm.id,
        configId,
        title: activeForm.title.trim(),
        slug: slugCandidate,
        description: activeForm.description?.trim() || undefined,
        submitButtonText: activeForm.submitButtonText?.trim() || 'Gönder',
        successMessage: activeForm.successMessage?.trim() || 'Mesajınız iletildi. Teşekkür ederiz.',
        notifyEmail: activeForm.notifyEmail?.trim() || undefined,
        fieldsJson: JSON.stringify(activeForm.fields),
        isActive: activeForm.isActive,
        displayOrder: activeForm.displayOrder
      };
      const res = await saveCustomForm(payload);
      const savedRaw = res?.data;
      if (savedRaw) {
        const saved = fromApi(savedRaw);
        setForms((prev) => prev.map((f, idx) => (idx === activeIndex ? { ...saved } : f)));
      }
      setSnack({ open: true, message: 'Form kaydedildi', severity: 'success' });
    } catch (e: any) {
      setSnack({
        open: true,
        message: e?.response?.data?.message || e?.message || 'Kayıt sırasında hata oluştu',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete.id) return;
    try {
      if (!confirmDelete.isNew) {
        await deleteCustomForm(confirmDelete.id);
      }
      setForms((prev) => prev.filter((f) => f.id !== confirmDelete.id));
      setActiveIndex(0);
      setSnack({ open: true, message: 'Form silindi', severity: 'success' });
    } catch (e: any) {
      setSnack({
        open: true,
        message: e?.response?.data?.message || e?.message || 'Silme sırasında hata oluştu',
        severity: 'error'
      });
    } finally {
      setConfirmDelete({ open: false });
    }
  };

  // ----- Field mutation helpers -----
  const addField = () => {
    const f: CustomFormField = { id: newFieldId(), type: 'text', label: 'Yeni Alan', required: false, placeholder: '' };
    updateActive({ fields: [...(activeForm?.fields || []), f] });
  };
  const removeField = (id: string) => {
    if (!activeForm) return;
    updateActive({ fields: activeForm.fields.filter((f) => f.id !== id) });
  };
  const moveField = (index: number, dir: -1 | 1) => {
    if (!activeForm) return;
    const next = [...activeForm.fields];
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    [next[index], next[newIdx]] = [next[newIdx], next[index]];
    updateActive({ fields: next });
  };
  const updateField = (id: string, patch: Partial<CustomFormField>) => {
    if (!activeForm) return;
    updateActive({
      fields: activeForm.fields.map((f) => (f.id === id ? { ...f, ...patch } : f))
    });
  };

  const previewSlug = useMemo(() => {
    if (!activeForm) return '';
    return activeForm.slug?.trim() || slugify(activeForm.title || '');
  }, [activeForm]);

  // -------------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------------
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DynamicFormOutlinedIcon color="primary" />
          <Typography variant="h6">Formlar</Typography>
        </Box>
        <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={handleAddForm} disabled={loading}>
          Yeni Form
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Storefront üzerinde göstermek istediğiniz formları burada tanımlayın
        (örn. Franchise Başvurusu, İletişim). Bu formları "Sayfalar" ekranında zengin metin
        editörünün üzerindeki "Form Ekle" butonuyla içeriğe yerleştirebilirsiniz. Gelen yanıtlar
        "Gelen Cevaplar" sekmesinden görüntülenebilir.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && forms.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 2 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Henüz form oluşturmadınız.
          </Typography>
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleAddForm}>
            İlk Formu Oluştur
          </Button>
        </Box>
      )}

      {!loading && forms.length > 0 && (
        <>
          <Tabs
            value={activeIndex}
            onChange={(_, v) => { setActiveIndex(v); setActiveSubTab('edit'); }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: '1px solid #e2e8f0', mb: 2 }}
          >
            {forms.map((f, idx) => (
              <Tab
                key={f.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{f.title || `Form #${idx + 1}`}</span>
                    {f.isDirty && (
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'warning.main' }} />
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>

          {activeForm && (
            <>
              <Tabs
                value={activeSubTab}
                onChange={(_, v) => setActiveSubTab(v as 'edit' | 'submissions')}
                sx={{ mb: 2 }}
              >
                <Tab value="edit" label="Form Tanımı" icon={<DynamicFormOutlinedIcon />} iconPosition="start" />
                <Tab
                  value="submissions"
                  label="Gelen Cevaplar"
                  icon={<InboxIcon />}
                  iconPosition="start"
                  disabled={!!activeForm.isNew}
                />
              </Tabs>

              {activeSubTab === 'edit' ? (
                <FormEditor
                  form={activeForm}
                  previewSlug={previewSlug}
                  onChange={updateActive}
                  onAddField={addField}
                  onRemoveField={removeField}
                  onMoveField={moveField}
                  onUpdateField={updateField}
                  onSave={handleSave}
                  onDelete={() => setConfirmDelete({
                    open: true, id: activeForm.id, isNew: activeForm.isNew, title: activeForm.title
                  })}
                  saving={saving}
                />
              ) : (
                <SubmissionsPanel
                  formId={activeForm.id}
                  formTitle={activeForm.title}
                  formFields={activeForm.fields}
                />
              )}
            </>
          )}
        </>
      )}

      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false })}>
        <DialogTitle>Formu Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{confirmDelete.title}" formunu kalıcı olarak silmek istediğinize emin misiniz?
            Bu formun gelen yanıtları da etkilenebilir.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false })}>Vazgeç</Button>
          <Button color="error" onClick={handleDelete}>Sil</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>

      <Box component="span" sx={{ display: 'none' }}>{companyId}</Box>
    </Paper>
  );
};

// =============================================================================
// Sub-component: FormEditor
// =============================================================================

interface FormEditorProps {
  form: FormItem;
  previewSlug: string;
  onChange: (patch: Partial<FormItem>) => void;
  onAddField: () => void;
  onRemoveField: (id: string) => void;
  onMoveField: (index: number, dir: -1 | 1) => void;
  onUpdateField: (id: string, patch: Partial<CustomFormField>) => void;
  onSave: () => void;
  onDelete: () => void;
  saving: boolean;
}

const FormEditor: React.FC<FormEditorProps> = ({
  form, previewSlug, onChange,
  onAddField, onRemoveField, onMoveField, onUpdateField,
  onSave, onDelete, saving
}) => {
  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TextField
            label="Form Başlığı"
            fullWidth
            size="small"
            value={form.title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder="Örn. Franchise Başvuru Formu"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Kısa Ad (slug)"
            fullWidth
            size="small"
            value={form.slug}
            onChange={(e) => onChange({ slug: e.target.value })}
            placeholder={previewSlug || 'otomatik-uretilir'}
            helperText={`Kullanılacak slug: ${previewSlug || '—'}`}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Açıklama (formun üstünde görünür)"
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={form.description}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            label="Gönder Butonu Yazısı"
            fullWidth
            size="small"
            value={form.submitButtonText}
            onChange={(e) => onChange({ submitButtonText: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            label="Başarı Mesajı"
            fullWidth
            size="small"
            value={form.successMessage}
            onChange={(e) => onChange({ successMessage: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Sıralama"
            type="number"
            fullWidth
            size="small"
            value={form.displayOrder}
            onChange={(e) => onChange({ displayOrder: Number(e.target.value) || 0 })}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Bildirim E-postası (opsiyonel)"
            fullWidth
            size="small"
            value={form.notifyEmail}
            onChange={(e) => onChange({ notifyEmail: e.target.value })}
            helperText="Form gönderildiğinde içerik bu e-posta adresine de iletilir."
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={(e) => onChange({ isActive: e.target.checked })}
              />
            }
            label="Aktif"
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Form Alanları</Typography>
        <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={onAddField}>
          Alan Ekle
        </Button>
      </Box>

      {form.fields.length === 0 && (
        <Alert severity="info">Henüz alan yok. "Alan Ekle" ile başlayın.</Alert>
      )}

      <Stack spacing={2}>
        {form.fields.map((field, idx) => (
          <FieldRow
            key={field.id}
            field={field}
            index={idx}
            total={form.fields.length}
            onMove={onMoveField}
            onRemove={onRemoveField}
            onUpdate={onUpdateField}
          />
        ))}
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button color="error" startIcon={<DeleteIcon />} onClick={onDelete} disabled={saving}>
          Sil
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={onSave}
          disabled={saving}
        >
          {saving ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </Box>
    </Box>
  );
};

// =============================================================================
// Sub-component: FieldRow
// =============================================================================

interface FieldRowProps {
  field: CustomFormField;
  index: number;
  total: number;
  onMove: (index: number, dir: -1 | 1) => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, patch: Partial<CustomFormField>) => void;
}

const FieldRow: React.FC<FieldRowProps> = ({ field, index, total, onMove, onRemove, onUpdate }) => {
  const showOptions = HAS_OPTIONS(field.type);
  const optionsString = (field.options || []).join('\n');
  const [optionsDraft, setOptionsDraft] = useState(optionsString);

  useEffect(() => {
    setOptionsDraft(optionsString);
  }, [field.id, optionsString]);

  const parseOptionsInput = (raw: string): string[] =>
    raw
      // Enter ile satır satır girişin yanında / , ; ayraçlarını da destekle.
      .split(/\r?\n|\/|,|;/g)
      .map((s) => s.trim())
      .filter(Boolean);
  const handleTypeChange = (nextType: CustomFormFieldType) => {
    const patch: Partial<CustomFormField> = { type: nextType };

    // Hazır şehir/ilçe alanı seçildiğinde varsayılanları daha anlaşılır hale getir.
    if (nextType === 'city_district') {
      const normalizedLabel = (field.label || '').trim().toLowerCase();
      if (!field.label || normalizedLabel === 'yeni alan') {
        patch.label = 'Şehir / İlçe';
      }
      if (!field.placeholder) {
        patch.placeholder = 'Şehir seçiniz';
      }
    }

    onUpdate(field.id, patch);
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Alan Türü</InputLabel>
              <Select
                label="Alan Türü"
                value={field.type}
                onChange={(e) => handleTypeChange(e.target.value as CustomFormFieldType)}
              >
                {FIELD_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              size="small"
              fullWidth
              label="Etiket"
              value={field.label}
              onChange={(e) => onUpdate(field.id, { label: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              size="small"
              fullWidth
              label="Yer Tutucu"
              value={field.placeholder || ''}
              onChange={(e) => onUpdate(field.id, { placeholder: e.target.value })}
              disabled={field.type === 'checkbox' || field.type === 'radio'}
            />
          </Grid>
          <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tooltip title="Zorunlu">
              <Switch
                checked={field.required}
                onChange={(e) => onUpdate(field.id, { required: e.target.checked })}
              />
            </Tooltip>
          </Grid>

          {showOptions && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Seçenekler (her satıra bir tane)"
                value={optionsDraft}
                onChange={(e) => setOptionsDraft(e.target.value)}
                onBlur={() => {
                  const parsed = parseOptionsInput(optionsDraft);
                  onUpdate(field.id, { options: parsed });
                  setOptionsDraft(parsed.join('\n'));
                }}
                onKeyDown={(e) => {
                  // Enter tuşu parent kısayollarına takılmadan alt satıra geçsin.
                  if (e.key === 'Enter') e.stopPropagation();
                }}
                helperText="Enter ile alt satıra geçebilirsiniz. Ayrıca / , ; ile ayırma da desteklenir."
              />
            </Grid>
          )}

          {field.type === 'city_district' && (
            <Grid item xs={12}>
              <Alert severity="info">
                Bu alan tipi site ön yüzünde otomatik şehir ve ilçe listesi gösterir. Seçenek girmeniz gerekmez.
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Yardım metni (opsiyonel)"
              value={field.helpText || ''}
              onChange={(e) => onUpdate(field.id, { helpText: e.target.value })}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Yukarı">
              <span>
                <IconButton size="small" onClick={() => onMove(index, -1)} disabled={index === 0}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Aşağı">
              <span>
                <IconButton size="small" onClick={() => onMove(index, 1)} disabled={index === total - 1}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Tooltip title="Alanı Sil">
            <IconButton size="small" color="error" onClick={() => onRemove(field.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// Sub-component: SubmissionsPanel (server-side paginated)
// =============================================================================

interface SubmissionsPanelProps {
  formId: string;
  formTitle: string;
  formFields: CustomFormField[];
}

const SubmissionsPanel: React.FC<SubmissionsPanelProps> = ({ formId, formTitle, formFields }) => {
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [items, setItems] = useState<CustomFormSubmission[]>([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!formId || formId.startsWith('new_')) return;
    try {
      setLoading(true);
      setError(null);
      const res = await getCustomFormSubmissions(formId, page, pageSize);
      setItems(Array.isArray(res?.data) ? res.data : []);
      setTotal(Number(res?.totalCount ?? 0));
      setUnread(Number(res?.unreadCount ?? 0));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Yanıtlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [formId, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const fieldLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    formFields.forEach((f) => {
      if (f?.id) map[f.id] = f.label || f.id;
    });
    return map;
  }, [formFields]);
  const normalizedFieldLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    formFields.forEach((f) => {
      const normalized = normalizeFieldKey(f?.id || '');
      if (normalized) {
        map[normalized] = f.label || f.id;
      }
    });
    return map;
  }, [formFields]);

  const formatValue = (v: unknown): string => {
    if (Array.isArray(v)) return v.map((x) => String(x ?? '')).join(', ');
    if (v && typeof v === 'object') {
      const obj = v as Record<string, unknown>;
      if ('city' in obj || 'district' in obj) {
        const city = String(obj.city ?? '').trim();
        const district = String(obj.district ?? '').trim();
        if (city && district) return `${city} / ${district}`;
        if (city) return city;
        if (district) return district;
      }
      return Object.entries(obj)
        .map(([k, val]) => `${k}: ${String(val ?? '')}`)
        .join(', ');
    }
    return String(v ?? '');
  };

  const resolveLabelsForEntries = (entries: Array<[string, unknown]>): Record<string, string> => {
    const usedLabels = new Set<string>();
    const unresolvedKeys: string[] = [];
    const resolvedLabelByKey: Record<string, string> = {};

    for (const [key] of entries) {
      const directLabel = fieldLabelMap[key];
      const normalizedLabel = normalizedFieldLabelMap[normalizeFieldKey(key)];
      const resolved = directLabel || normalizedLabel;
      if (resolved) {
        resolvedLabelByKey[key] = resolved;
        usedLabels.add(resolved);
      } else {
        unresolvedKeys.push(key);
      }
    }

    if (unresolvedKeys.length > 0) {
      const fallbackLabels = formFields
        .map((f) => (f.label || '').trim())
        .filter(Boolean)
        .filter((label) => !usedLabels.has(label));

      unresolvedKeys.forEach((key, index) => {
        resolvedLabelByKey[key] = fallbackLabels[index] || `Alan ${index + 1}`;
      });
    }

    return resolvedLabelByKey;
  };

  const getSubmissionPreview = (raw?: string | null): string => {
    if (!raw) return '';
    try {
      const obj = JSON.parse(raw);
      if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return '';
      const entries = Object.entries(obj as Record<string, unknown>);
      if (entries.length === 0) return '';
      const resolvedLabelByKey = resolveLabelsForEntries(entries);
      const normalizeForMatch = (value: string): string =>
        String(value || '')
          .toLocaleLowerCase('tr-TR')
          .replace(/ı/g, 'i')
          .replace(/ş/g, 's')
          .replace(/ğ/g, 'g')
          .replace(/ü/g, 'u')
          .replace(/ö/g, 'o')
          .replace(/ç/g, 'c')
          .replace(/[^a-z0-9]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();

      const isNameLike = (label: string): boolean => {
        const normalized = normalizeForMatch(label);
        return (
          (normalized.includes('ad') && normalized.includes('soyad')) ||
          (normalized.includes('isim') && normalized.includes('soyisim')) ||
          normalized.includes('ad soyad') ||
          normalized.includes('full name') ||
          (normalized.includes('name') && normalized.includes('surname'))
        );
      };

      const toPreview = (label: string, value: unknown): string => {
        const textValue = formatValue(value).trim();
        if (!textValue) return '';
        const shortValue = textValue.length > 40 ? `${textValue.slice(0, 40)}...` : textValue;
        return `${label}: ${shortValue}`;
      };

      // 1) Önce ad-soyad benzeri alanı bul.
      for (const [key, value] of entries) {
        const label = resolvedLabelByKey[key] || fieldLabelMap[key] || key;
        if (!isNameLike(label)) continue;
        const preview = toPreview(label, value);
        if (preview) return preview;
      }

      // 2) Bulunamazsa ilk dolu alanı göster (boş görünmesin).
      for (const [key, value] of entries) {
        const label = resolvedLabelByKey[key] || fieldLabelMap[key] || key;
        const preview = toPreview(label, value);
        if (preview) return preview;
      }

      return '';
    } catch {
      return '';
    }
  };

  const renderData = (raw?: string | null) => {
    if (!raw) return <Typography variant="body2" color="text.secondary">(Boş)</Typography>;
    try {
      const obj = JSON.parse(raw);
      const entries = Object.entries(obj);
      if (entries.length === 0) return <Typography variant="body2" color="text.secondary">(Boş)</Typography>;
      const resolvedLabelByKey = resolveLabelsForEntries(entries);

      return (
        <Stack spacing={0.5}>
          {entries.map(([k, v]) => (
            <Box key={k} sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120, color: 'text.secondary' }}>
                {resolvedLabelByKey[k] || fieldLabelMap[k] || k}:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {formatValue(v)}
              </Typography>
            </Box>
          ))}
        </Stack>
      );
    } catch {
      return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{raw}</Typography>;
    }
  };

  const toggleRead = async (s: CustomFormSubmission) => {
    try {
      await markCustomFormSubmissionRead(s.id, !s.isRead);
      setItems((prev) => prev.map((it) => (it.id === s.id ? { ...it, isRead: !s.isRead } : it)));
      setUnread((u) => (s.isRead ? u + 1 : Math.max(0, u - 1)));
    } catch {
      // sessizce yutmuyoruz; hata UI'da göstermek için error state'i set edebiliriz
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu yanıtı kalıcı olarak silmek istediğinize emin misiniz?')) return;
    try {
      await deleteCustomFormSubmission(id);
      await fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Silme hatası');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          "{formTitle}" - Gelen Cevaplar
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Yenile">
            <span>
              <IconButton size="small" onClick={fetchData} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Chip size="small" label={`Toplam: ${total}`} />
          <Chip size="small" color="warning" label={`Okunmamış: ${unread}`} />
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && items.length === 0 && (
        <Alert severity="info">Henüz gönderim yok.</Alert>
      )}

      <Stack spacing={1}>
        {items.map((s) => {
          const preview = getSubmissionPreview(s.dataJson);
          return (
            <Card key={s.id} variant="outlined" sx={{ borderLeft: s.isRead ? '3px solid #cbd5e1' : '3px solid #f59e0b' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatSubmissionDate(s.createdAt)}
                    </Typography>
                    {preview && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.25,
                          color: 'text.secondary',
                          maxWidth: 560,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        Önizleme: {preview}
                      </Typography>
                    )}
                  </Box>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title={s.isRead ? 'Okunmadı olarak işaretle' : 'Okundu olarak işaretle'}>
                    <IconButton size="small" onClick={() => toggleRead(s)}>
                      {s.isRead ? <MarkUnreadIcon fontSize="small" /> : <MarkReadIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sil">
                    <IconButton size="small" color="error" onClick={() => handleDelete(s.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setExpanded((e) => (e === s.id ? null : s.id))}
                  >
                    {expanded === s.id ? 'Gizle' : 'Detay'}
                  </Button>
                </Stack>
              </Box>
              {expanded === s.id && <Box sx={{ mt: 1 }}>{renderData(s.dataJson)}</Box>}
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {total > pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            page={page}
            count={totalPages}
            onChange={(_, p) => setPage(p)}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};

export default FormsSettings;
